/**
 * 端末表示設定の同期 (Firestore 購読 + API 書込) を有効化するかのゲート。
 *
 * 背景: Netlify は 1 サイト・環境変数が単一のため、 STG ビルドも本番 Rails URL を指す。
 * STG で動かすと本番 DB を汚すため、 **STG では絶対に動かさない**。 本番でも端末ごとに
 * 段階解放できるようにする。
 *
 * 判定は 2 段 (両方満たす端末でのみ有効):
 *   1) 本番ドメインか (PROD_HOSTS)。 STG / Netlify preview / localhost は常に無効。
 *   2) 解放対象の端末か (ENABLED_GATEWAY_IDS)。
 *
 * 段階解放の手順 (この 2 定数を編集して deploy):
 *   - 準備/検証前 : PROD_HOSTS=[本番ドメイン], ENABLED_GATEWAY_IDS=[]        → 全端末 OFF
 *   - 1 端末検証  : ENABLED_GATEWAY_IDS=["<gateway_id>"]                    → その端末のみ ON
 *   - 解放        : ENABLED_GATEWAY_IDS=["*"]                              → 全端末 ON
 *
 * フェイルセーフ: PROD_HOSTS / ENABLED_GATEWAY_IDS が空の間は常に無効 (誤書込なし)。
 */

// ⚠️ 本番の adbox 配信アプリのドメインのみを入れること (STG ドメインは絶対に入れない)。
//    空の間は全環境で無効。
const PROD_HOSTS: string[] = ["twa.app.xadbox.cloud"];

// 段階解放の許可リスト。 [] = 全OFF / ["<gateway_id>", ...] = 指定端末のみ / ["*"] = 全端末。
// 検証端末で確認後、 2026-07-03 に本番全端末解放 (["*"])。 緊急時は特定端末のみ / [] へ戻せる。
const ENABLED_GATEWAY_IDS: string[] = ["*"];

// ローカル開発サーバ (npm run serve) は NODE_ENV=development。 STG/本番ビルドは
// production なので、 これで「ローカル検証」だけを区別できる (STG ビルドは無効のまま)。
//
// export している用途: 設定画面の端末選択は本番では「検索 (Rails API) → 選択」だが、
// ローカルでは その API に繋がらず端末を確定できない。 dev のみ gateway_id を直接手入力
// できる欄を出すのに使う (本番ビルドでは false なので手入力欄は出ない)。 実 Firestore は
// ローカルからも購読できるため、 実端末 ID を入れれば本物の配信データで確認できる。
export const isDevServer = (): boolean => process.env.NODE_ENV !== "production";

const isProdHost = (): boolean =>
  typeof window !== "undefined" &&
  PROD_HOSTS.includes(window.location.hostname);

/**
 * 環境レベルの有効判定 (端末非依存)。 ローカル開発 または 本番ドメインで true。
 * STG / Netlify preview (production build かつ非本番ホスト) では false。
 * → terminalId 確定前の API 呼び出し (端末選択の検索) の可否に使う。
 */
export const isDeviceSettingsEnvEnabled = (): boolean =>
  isDevServer() || isProdHost();

/**
 * この端末で表示設定の同期 (Firestore 購読 + API 書込) を動かしてよいか。
 * 環境が有効 (ローカル開発 or 本番ドメイン) かつ 解放対象端末 のときだけ true。
 * ローカル開発は検証のため全端末許可する。
 */
export const isDeviceSettingsSyncEnabled = (terminalId: string): boolean => {
  if (!terminalId) return false;
  if (isDevServer()) return true;
  if (!isProdHost()) return false;
  return (
    ENABLED_GATEWAY_IDS.includes("*") ||
    ENABLED_GATEWAY_IDS.includes(terminalId)
  );
};

// heartbeat (テレメトリ送信 + 設定の初回 backfill) の段階解放。 表示設定同期とは独立トグル。
// [] = 全OFF / ["<gateway_id>", ...] = 指定端末のみ / ["*"] = 全端末。
// 検証端末で確認後、 2026-07-03 に本番全端末解放 (["*"])。 緊急時は特定端末のみ / [] へ戻せる。
const HEARTBEAT_GATEWAY_IDS: string[] = ["*"];

/**
 * この端末で heartbeat を動かしてよいか。 環境が有効 (ローカル開発 or 本番ドメイン) かつ
 * 解放対象端末 のときだけ true。 表示設定同期 (ENABLED_GATEWAY_IDS) とは別トグル
 * (HEARTBEAT_GATEWAY_IDS) で制御するため、「手動保存を先に検証 → 後で heartbeat/backfill を
 * 有効化」 のように段階解放できる。 ローカル開発は検証のため全端末許可する。
 */
export const isHeartbeatEnabled = (terminalId: string): boolean => {
  if (!terminalId) return false;
  if (isDevServer()) return true;
  if (!isProdHost()) return false;
  return (
    HEARTBEAT_GATEWAY_IDS.includes("*") ||
    HEARTBEAT_GATEWAY_IDS.includes(terminalId)
  );
};

// リリース自己リロード (public/release-watch.js) の段階解放。他のトグルとは独立。
//
// ⚠ gate は **バンドルに焼き込まれる**ので、ここを変えただけでは効かない。対象端末が
//   この版を読み込んで初めて有効になる (§12.7)。つまり ["*"] にしても「全端末が一斉に
//   watcher を持つ」瞬間は存在せず、リロードした端末から順に有効になるだけ。
//   常時起動でリロードしない端末は `rails delivery:bootstrap_reload` で個別に起こす。
//
// 経緯: 2026-09-08 検証端末のみ → 09-09 実機 2 台 → 09-10 本番で自己リロードを実証
//       (233835 がリリース 12 分後に自動反映) → 09-11 割合解放 10% → 09-12 全端末解放。
//
// 全端末解放に踏み切った理由 (2026-09-12 の本番実測):
//   - オンライン 523 枠のうち watcher を積んでいるのは 333 枠 (63.7%)。残り 190 枠は
//     09-07 より前のバンドルのままで、**仕組みそのものが入っていない**
//   - この 190 枠を起こす予約リロードは「起こした先のバンドルの gate」で運命が決まる。
//     10% のまま起こすと 9 割が armed にならず、もう一度起こす羽目になる
//   - 諦め報告は 0 台、リロードのループも観測なし
//
// 段階解放へ戻すときは "*" を外す。割合解放 (RELEASE_RELOAD_PERCENT) に戻る。
// 検証に使った実機: 233835 (毎晩 0 時に自力リロード) / 026_lobby (対照・確実に稼働中) /
//                   043_toilet_m (常時起動で 09-08 から一度もリロードしていない本命)
const RELEASE_RELOAD_GATEWAY_IDS: string[] = ["*"];

// 明示リスト以外をどれだけ解放するか (0〜100)。0 で割合解放は無効。
// 端末は gateway_id のハッシュで 0〜99 のバケットに割り当てられ、この値未満だけ有効になる。
//   - **決定的**: 同じ端末は常に同じバケット。リリースをまたいでも対象が入れ替わらない
//   - **単調**: 値を上げると対象は増えるだけ。既に有効だった端末が外れない
// 本番 774 枠での実測: 10 → 80 枠 (10.3%) / 25 → 192 枠 (24.8%) / 50 → 383 枠 (49.5%)。
// ⚠ 現在は上の ["*"] が先に効くため使われない。段階解放へ戻したときの既定値。
const RELEASE_RELOAD_PERCENT = 10;

/**
 * gateway_id を 0〜99 のバケットへ落とす (FNV-1a + 最終攪拌)。
 *
 * ⚠ 単純な djb2 では**連番の gateway_id (233835, 233836...) が同じ帯に固まる**。
 *   本番の実 ID で測ったところ 10% 設定で実際 12.5%、帯ごとの偏りが 1.5 倍以上あった。
 *   末尾文字の差が下位ビットにそのまま出るため。最終攪拌で上位ビットを下位へ混ぜると
 *   偏りが 8.8〜11.1% に収まることを実 ID で確認している。
 */
export const releaseReloadBucket = (terminalId: string): number => {
  let h = 2166136261;
  for (let i = 0; i < terminalId.length; i++) {
    h = (h ^ terminalId.charCodeAt(i)) >>> 0;
    h = Math.imul(h, 16777619) >>> 0;
  }
  h = (h ^ (h >>> 16)) >>> 0;
  h = Math.imul(h, 2246822507) >>> 0;
  h = (h ^ (h >>> 13)) >>> 0;
  return h % 100;
};

/**
 * この端末でリリース自己リロードを動かしてよいか。watcher はバンドル外の素 JS で
 * この関数を import できないため、判定結果は useReleaseReload が localStorage
 * (adbox_release.enabled) に書き、watcher はそれを読む。
 */
export const isReleaseReloadEnabled = (terminalId: string): boolean => {
  if (!terminalId) return false;
  if (isDevServer()) return true;
  if (!isProdHost()) return false;
  if (RELEASE_RELOAD_GATEWAY_IDS.includes("*")) return true;
  if (RELEASE_RELOAD_GATEWAY_IDS.includes(terminalId)) return true;
  return releaseReloadBucket(terminalId) < RELEASE_RELOAD_PERCENT;
};
